// MeshPlan.ts — PURE 3D mesh data type + append builder for the Buildings Set1 P3 geometry layer
// (§4). No three / mobx-graphlink / DOM imports — the Node vitest lane imports this directly, and
// the renderer (`Building3D.tsx`) converts a finished `MeshPlan` into a `THREE.BufferGeometry`.
//
// A `MeshPlan` is NON-INDEXED: `positions`/`normals`/`uvs` are parallel flat arrays (every 3
// floats = one vertex, every 2 floats = one uv), and `groups` describes runs of consecutive
// vertices that share a single `materialSlot` (a SEMANTIC slot name like "wallExterior" or
// "floorTop" — the renderer maps slot → recipe id via the room's resolved materials, so identical
// geometry can be shared across instances even when their materials differ). Each group's `count`
// is a number of vertices (a multiple of 3 = triangles).
export type MeshPlan = {
	positions: number[]; // flat xyz
	normals: number[];   // flat xyz
	uvs: number[];       // flat uv
	groups: Array<{materialSlot: string; start: number; count: number}>;
};

export type V3 = [number, number, number];
export type UV = [number, number];

/** Incremental `MeshPlan` builder. Vertices are appended with an explicit normal (so winding and
 *  the shading normal never disagree), and consecutive vertices sharing a material slot are
 *  coalesced into one group (so the renderer emits one draw call per slot per geometry). */
export class MeshBuilder {
	positions: number[] = [];
	normals: number[] = [];
	uvs: number[] = [];
	groups: Array<{materialSlot: string; start: number; count: number}> = [];
	private currentSlot = "";
	private currentStart = 0;

	/** Switch the active material slot; closes the previous group if it has vertices. */
	setMaterialSlot(slot: string): this {
		if (slot != this.currentSlot) {
			this.flushGroup();
			this.currentSlot = slot;
			this.currentStart = this.positions.length / 3;
		}
		return this;
	}

	private flushGroup(): void {
		const count = this.positions.length / 3 - this.currentStart;
		if (this.currentSlot != "" && count > 0) {
			this.groups.push({materialSlot: this.currentSlot, start: this.currentStart, count});
		}
	}

	/** Push a single vertex. */
	vertex(p: V3, n: V3, uv: UV): this {
		this.positions.push(p[0], p[1], p[2]);
		this.normals.push(n[0], n[1], n[2]);
		this.uvs.push(uv[0], uv[1]);
		return this;
	}

	/** Push one triangle (a,b,c) sharing normal `n` (and per-vertex uvs).
	 *
	 *  WINDING ENFORCEMENT: three.js `FrontSide` culling uses the triangle's geometric winding
	 *  (screen-space orientation), NOT the supplied normal. So we orient the winding to agree with
	 *  `n`: if the geometric normal `(b-a)×(c-a)` is anti-parallel to `n`, we swap `b`/`c` so the
	 *  emitted face is front-facing from the side `n` points toward. Without this, a face whose
	 *  author passed a normal but left the winding matching some other convention renders as a
	 *  back-face — the Buildings "inside of wall invisible from inside" bug (inner wall faces were
	 *  wound identically to the outer faces, so `FrontSide` culled them when viewed from inside).
	 *  Area (cross-product magnitude) and undirected edge counts are invariant to this swap, so
	 *  the MeshPlanArea/MeshPlanEdges tests are unaffected. */
	tri(a: V3, b: V3, c: V3, n: V3, uva: UV, uvb: UV, uvc: UV): this {
		// Geometric normal via right-hand rule.
		const abx = b[0] - a[0], aby = b[1] - a[1], abz = b[2] - a[2];
		const acx = c[0] - a[0], acy = c[1] - a[1], acz = c[2] - a[2];
		const gx = aby * acz - abz * acy;
		const gy = abz * acx - abx * acz;
		const gz = abx * acy - aby * acx;
		const dot = gx * n[0] + gy * n[1] + gz * n[2];
		if (dot < 0) {
			// Winding is backward relative to the normal — swap b/c so the front face points toward n.
			const tb = b, tuvb = uvb;
			b = c; c = tb;
			uvb = uvc; uvc = tuvb;
		}
		this.vertex(a, n, uva);
		this.vertex(b, n, uvb);
		this.vertex(c, n, uvc);
		return this;
	}

	/** Push a quad (a,b,c,d) as two triangles (a,b,c)+(a,c,d) sharing normal `n`. */
	quad(a: V3, b: V3, c: V3, d: V3, n: V3, uva: UV, uvb: UV, uvc: UV, uvd: UV): this {
		this.tri(a, b, c, n, uva, uvb, uvc);
		this.tri(a, c, d, n, uva, uvc, uvd);
		return this;
	}

	/** Append another `MeshPlan` in its entirety (vertices + groups, with group offsets remapped).
	 *  Material-slot groups are copied verbatim, so sub-plans keep their own slot tags after the
	 *  merge (used by the P4 door/window model assembly). */
	append(plan: MeshPlan): this {
		const offset = this.positions.length / 3;
		this.positions.push(...plan.positions);
		this.normals.push(...plan.normals);
		this.uvs.push(...plan.uvs);
		// Reset the coalescing state so a boundary vertex doesn't accidentally merge with the next
		// externally-pushed slot; the caller's slot switches drive grouping from here.
		this.currentSlot = "";
		this.currentStart = this.positions.length / 3;
		for (const g of plan.groups) {
			this.groups.push({materialSlot: g.materialSlot, start: g.start + offset, count: g.count});
		}
		return this;
	}

	build(): MeshPlan {
		this.flushGroup();
		return {positions: this.positions, normals: this.normals, uvs: this.uvs, groups: this.groups};
	}
}

/** Total surface area of a `MeshPlan`, summed over every emitted triangle (twice the cross-product
 *  magnitude / 2 per triangle). Used by the §4.2 area-conservation tests. */
export function MeshPlanArea(plan: MeshPlan): number {
	let area = 0;
	const P = plan.positions;
	for (let i = 0; i < P.length; i += 9) {
		const ax = P[i], ay = P[i + 1], az = P[i + 2];
		const bx = P[i + 3], by = P[i + 4], bz = P[i + 5];
		const cx = P[i + 6], cy = P[i + 7], cz = P[i + 8];
		const ux = bx - ax, uy = by - ay, uz = bz - az;
		const vx = cx - ax, vy = cy - ay, vz = cz - az;
		const cx1 = uy * vz - uz * vy;
		const cy1 = uz * vx - ux * vz;
		const cz1 = ux * vy - uy * vx;
		area += Math.hypot(cx1, cy1, cz1) * 0.5;
	}
	return area;
}

/** Collect every undirected edge (as a sorted key `minV,maxV`) of a `MeshPlan`, for the §10
 *  watertightness test (a closed orientable surface has every edge appearing exactly twice). */
export function MeshPlanEdges(plan: MeshPlan): Map<string, number> {
	const P = plan.positions;
	const counts = new Map<string, number>();
	for (let i = 0; i < P.length; i += 9) {
		const corners = [
			[P[i], P[i + 1], P[i + 2]],
			[P[i + 3], P[i + 4], P[i + 5]],
			[P[i + 6], P[i + 7], P[i + 8]],
		];
		for (let e = 0; e < 3; e++) {
			const a = corners[e];
			const b = corners[(e + 1) % 3];
			const ka = `${a[0].toFixed(4)},${a[1].toFixed(4)},${a[2].toFixed(4)}`;
			const kb = `${b[0].toFixed(4)},${b[1].toFixed(4)},${b[2].toFixed(4)}`;
			const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
			counts.set(key, (counts.get(key) ?? 0) + 1);
		}
	}
	return counts;
}

// ---------------------------------------------------------------------------
// P4 transforms (door/window model placement)
// ---------------------------------------------------------------------------
/** Applies a rigid 3D transform (translation + rotation about world axes) to a `MeshPlan`,
 *  transforming both positions and normals, and optionally translating the UVs by `uvOffset`.
 *  Pure and node-testable. Used by the P4 door/window models to place a model authored in its local
 *  frame into a specific wall-segment frame. */
export function TransformMeshPlan(
	plan: MeshPlan,
	transform: {pos?: V3, rot?: Euler3, uvOffset?: [number, number]},
): MeshPlan {
	const p = transform.pos ?? [0, 0, 0];
	const rot = transform.rot ?? [0, 0, 0];
	const uvOff = transform.uvOffset ?? [0, 0];
	const R = rotationMatrix(rot);
	const nrm = R; // normals rotate by the same matrix (rigid)

	const out: MeshPlan = {positions: [], normals: [], uvs: [], groups: []};
	const P = plan.positions, N = plan.normals, U = plan.uvs;
	for (let i = 0, k = 0; i < P.length; i += 3, k += 2) {
		const x = P[i], y = P[i + 1], z = P[i + 2];
		out.positions.push(R[0] * x + R[1] * y + R[2] * z + p[0],
		                   R[3] * x + R[4] * y + R[5] * z + p[1],
		                   R[6] * x + R[7] * y + R[8] * z + p[2]);
		const nx = N[i], ny = N[i + 1], nz = N[i + 2];
		out.normals.push(R[0] * nx + R[1] * ny + R[2] * nz,
		                 R[3] * nx + R[4] * ny + R[5] * nz,
		                 R[6] * nx + R[7] * ny + R[8] * nz);
		out.uvs.push(U[k] + uvOff[0], U[k + 1] + uvOff[1]);
	}
	for (const g of plan.groups) out.groups.push({...g});
	return out;
}

/** A rigid Euler rotation: [rx, ry, rz] in RADIANS about the X, Y, then Z axes (applied in that
 *  order, matching three.js `Euler` default order but left-handed-Z-up-agnostic — for the 
 *  building models we only ever use single-axis rotations, so the order is unambiguous). */
export type Euler3 = [number, number, number];

/** Transform a `MeshPlan` by an explicit orthonormal basis. This matches the P4 door/window model
 *  authoring frame: model +X = `t` (along wall), model +Y = `up` (world up), model +Z = `n` (wall
 *  OUTWARD normal). So world = `pos` + x·`t` + y·`up` + z·`n`. Normals are transformed by the same
 *  basis (rigid). Pure + node-testable. */
export function TransformPlanToBasis(
	plan: MeshPlan,
	basis: {t: V3, up: V3, n: V3, pos: V3, uvOffset?: [number, number]},
): MeshPlan {
	const up = basis.up;
	const uvOff = basis.uvOffset ?? [0, 0];
	// world = pos + x·t + y·up + z·n (columns: t (model x), up (model y), n (model z)).
	const M = [
		basis.t[0], up[0], basis.n[0],
		basis.t[1], up[1], basis.n[1],
		basis.t[2], up[2], basis.n[2],
	];
	const p = basis.pos;
	const out: MeshPlan = {positions: [], normals: [], uvs: [], groups: []};
	const P = plan.positions, N = plan.normals, U = plan.uvs;
	for (let i = 0, k = 0; i < P.length; i += 3, k += 2) {
		const x = P[i], y = P[i + 1], z = P[i + 2];
		out.positions.push(M[0] * x + M[1] * y + M[2] * z + p[0],
		                   M[3] * x + M[4] * y + M[5] * z + p[1],
		                   M[6] * x + M[7] * y + M[8] * z + p[2]);
		const nx = N[i], ny = N[i + 1], nz = N[i + 2];
		out.normals.push(M[0] * nx + M[1] * ny + M[2] * nz,
		                 M[3] * nx + M[4] * ny + M[5] * nz,
		                 M[6] * nx + M[7] * ny + M[8] * nz);
		out.uvs.push(U[k] + uvOff[0], U[k + 1] + uvOff[1]);
	}
	for (const g of plan.groups) out.groups.push({...g});
	return out;
}

/** Column-major-ish 3×3 rotation matrix (row-major flat 9 entries) for Euler [rx, ry, rz] about
 *  X, Y, Z in that order. */
function rotationMatrix([rx, ry, rz]: Euler3): [number, number, number, number, number, number, number, number, number] {
	const cx = Math.cos(rx), sx = Math.sin(rx);
	const cy = Math.cos(ry), sy = Math.sin(ry);
	const cz = Math.cos(rz), sz = Math.sin(rz);
	// R = Rz · Ry · Rx (row-major)
	const m00 = cy * cz;
	const m01 = sx * sy * cz - cx * sz;
	const m02 = cx * sy * cz + sx * sz;
	const m10 = cy * sz;
	const m11 = sx * sy * sz + cx * cz;
	const m12 = cx * sy * sz - sx * cz;
	const m20 = -sy;
	const m21 = sx * cy;
	const m22 = cx * cy;
	return [m00, m01, m02, m10, m11, m12, m20, m21, m22];
}

// ---------------------------------------------------------------------------
// P4 box primitive (door/window parts)
// ---------------------------------------------------------------------------
/** Axis-aligned box. `size` spans the full [0..w]×[0..h]×[0..d] extent from `origin`; `uAxis`
 *  (default [1,0,0]) is the local "width" direction and `vAxis` (default [0,1,0]) the "height"
 *  direction used to build the box's local frame — so a single `BoxMesh` can author a door slab,
 *  filler block or handle in any orientation by passing its face-normal frame. Every face is a
 *  unit quad (no vertex sharing), so the box is always watertight and area-conservative. UVs are
 *  in metres scaled to the face's actual size, so procedural materials tile continuously. */
export function BoxMesh(
	origin: V3,
	size: [number, number, number],
	slot: string,
	opts: {uAxis?: V3, vAxis?: V3, colorUV?: boolean} = {},
): MeshPlan {
	const w = size[0], h = size[1], d = size[2];
	const u = normalize(opts.uAxis ?? [1, 0, 0]);
	const v = normalize(opts.vAxis ?? [0, 1, 0]);
	// w-axis, h-axis, depth-axis frame.
	const depth = cross(u, v);
	const b = new MeshBuilder();
	// The box's local axes: `u` (width, size w), `v` (height, size h), `depth = u×v` (thickness,
	// size d). Each face is a unit quad spanning two of the three axes, pinned at the appropriate
	// origin / far extent plane.
	// NOTE (winding/far-face bug, 2026-08-24): the three POSITIVE faces (`+depth`, `+u`, `+v`) were
	// originally authored with all four corners starting at `origin`, so they collapsed onto the
	// same planes as their NEGATIVE counterparts (the `+depth` face sat at `depth=0` instead of
	// `depth=d`, etc.). Every face shared the `origin` corner and the far planes had NO face at all.
	// The box was a degenerate "corner fan" whose surface area (22 for a 1×2×3 box) was unchanged,
	// so the area-conservation tests still passed while the door/window models were missing their
	// exterior faces (the surface was invisible from the outward side, and the origin corner was
	// double-covered by six opposite faces → z-fighting). The fix offsets each positive face by its
	// own `+extent` (`+u·w`, `+v·h`, `+depth·d` respectively) so the six faces land on six distinct
	// planes, matching the negative faces' far counterparts.
	const faces: Array<{n: V3, corners: V3[], size: [number, number]}> = [
		{ // +wu face (normal +depth) — at the FAR depth plane (origin + depth·d)
			n: depth,
			corners: [
				add(origin, scale(depth, d)),
				add(origin, add(scale(depth, d), scale(u, w))),
				add(origin, add(scale(depth, d), add(scale(u, w), scale(v, h)))),
				add(origin, add(scale(depth, d), scale(v, h))),
			],
			size: [w, h],
		},
		{ // wu- face (normal -depth) — at the ORIGIN depth plane
			n: scale(depth, -1),
			corners: [origin, add(origin, scale(v, h)), add(origin, add(scale(v, h), scale(u, w))), add(origin, scale(u, w))],
			size: [w, h],
		},
		{ // +udv face (normal +u) — at the FAR width plane (origin + u·w)
			n: u,
			corners: [
				add(origin, scale(u, w)),
				add(origin, add(scale(u, w), scale(v, h))),
				add(origin, add(scale(u, w), add(scale(v, h), scale(depth, d)))),
				add(origin, add(scale(u, w), scale(depth, d))),
			],
			size: [h, d],
		},
		{ // udv- face (normal -u) — at the ORIGIN width plane
			n: scale(u, -1),
			corners: [origin, add(origin, scale(depth, d)), add(origin, add(scale(depth, d), scale(v, h))), add(origin, scale(v, h))],
			size: [h, d],
		},
		{ // +wv face... (normal +v) — at the FAR height plane (origin + v·h)
			n: v,
			corners: [
				add(origin, scale(v, h)),
				add(origin, add(scale(v, h), scale(u, w))),
				add(origin, add(scale(v, h), add(scale(u, w), scale(depth, d)))),
				add(origin, add(scale(v, h), scale(depth, d))),
			],
			size: [w, d],
		},
		{ // wv- face (normal -v)
			n: scale(v, -1),
			corners: [origin, add(origin, scale(depth, d)), add(origin, add(scale(depth, d), scale(u, w))), add(origin, scale(u, w))],
			size: [w, d],
		},
	];
	for (const f of faces) {
		const [A, B, C, D] = f.corners;
		const n = f.n;
		b.setMaterialSlot(slot);
		b.quad(A, B, C, D, n, [0, 0], [f.size[0], 0], [f.size[0], f.size[1]], [0, f.size[1]]);
	}
	return b.build();
}

function normalize(a: V3): V3 {
	const len = Math.hypot(a[0], a[1], a[2]) || 1;
	return [a[0] / len, a[1] / len, a[2] / len];
}
function scale(a: V3, s: number): V3 { return [a[0] * s, a[1] * s, a[2] * s]; }
function add(a: V3, b: V3): V3 { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
function cross(a: V3, b: V3): V3 {
	return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/** Partition a `MeshPlan` into two plans by material slot: vertices/polygons whose group belongs to
 *  any `splitSlots` go into `{included}`, everything else into `{excluded}`. Used by P4 to separate
 *  the windowGlass transparent pass from the opaque door/window/wall geometry. Pure + node-testable. */
export function SplitPlanBySlots(plan: MeshPlan, splitSlots: ReadonlySet<string>): {included: MeshPlan, excluded: MeshPlan} {
	const included: MeshPlan = {positions: [], normals: [], uvs: [], groups: []};
	const excluded: MeshPlan = {positions: [], normals: [], uvs: [], groups: []};
	for (const g of plan.groups) {
		const target = splitSlots.has(g.materialSlot) ? included : excluded;
		// Move the vertex slice for this group.
		const v0 = g.start * 3;
		const vn = g.count * 3;
		target.positions.push(...plan.positions.slice(v0, v0 + vn));
		target.normals.push(...plan.normals.slice(v0, v0 + vn));
		const u0 = g.start * 2;
		const un = g.count * 2;
		target.uvs.push(...plan.uvs.slice(u0, u0 + un));
		const start = target.positions.length / 3 - g.count;
		target.groups.push({materialSlot: g.materialSlot, start, count: g.count});
	}
	return {included, excluded};
}
